import { isSamePlanIgnoringEcom } from './feature';

interface PromotionInfo {
  campaignCode: string;
  endDate: number; // Unix timestamp in milliseconds
}

export interface StarterPackagePromotionInfo extends PromotionInfo {
  couponInfo?: Record<string, CouponConfig>;
  priceConfig?: PromotionPriceProps;
  bannerConfig?: PromotionBannerProps;
  discountPercentage: number;
}

export interface SingleProductPromotionInfo extends PromotionInfo {
  maxDiscountLicense: number;
  discountedProducts: DiscountProduct[];
  priceConfig?: PromotionPriceProps;
  bannerConfig?: PromotionBannerProps;
  packageGroups?: PackageGroup[];
  timeWindow?: TimeWindow;
}

// Union type for API responses that can return either promotion type
export type AnyPromotionInfo = SingleProductPromotionInfo | StarterPackagePromotionInfo;

/**
 * The campaign that prices a product, ecom-insensitively (the BE routes an eligible account's
 * legacy plan code to its `_ecom_` sibling).
 *
 * Several SINGLE_PRODUCT campaigns can be active at once - one per Starter SKU - so a row must
 * never borrow another product's campaign. `campaigns === undefined` means
 * `GET /v2/promotion/active-campaigns` has not answered yet: the single `/active` campaign then
 * stands in, which keeps a one-campaign environment behaving exactly as it did before.
 */
export const findPromotionForProduct = (
  campaigns: AnyPromotionInfo[] | undefined,
  activePromotion: AnyPromotionInfo | null | undefined,
  productCode: string,
): AnyPromotionInfo | null => {
  const discountsProduct = (promotion: AnyPromotionInfo) => (
    'discountedProducts' in promotion
    && promotion.discountedProducts.some(
      product => isSamePlanIgnoringEcom(product.productCode, productCode),
    )
  );
  const fallback = activePromotion && discountsProduct(activePromotion) ? activePromotion : null;

  if (campaigns === undefined) {
    return fallback;
  }

  return campaigns.find(discountsProduct) ?? fallback;
};

/** The campaign a preview's applied promotion belongs to, joined by campaign code. */
export const findPromotionByCampaignCode = (
  campaigns: AnyPromotionInfo[] | undefined,
  activePromotion: AnyPromotionInfo | null | undefined,
  campaignCode: string | undefined,
): AnyPromotionInfo | null => {
  if (!campaignCode) {
    return null;
  }
  return (campaigns ?? []).find(campaign => campaign.campaignCode === campaignCode)
    ?? (activePromotion?.campaignCode === campaignCode ? activePromotion : null);
};

interface DiscountProduct {
  productCode: string;
  discountQuantity: number;
  discountAmount: number;
  // One-time package discount: the BE grants this amount only when the purchase
  // redeems the full maxDiscountLicense quota (all-or-nothing starter package).
  onetimeDiscountAmount?: number;
}

export interface DiscountProductInfo {
  campaignCode: string;
  productCode: string;
  discountQuantity: number;
  discountAmount: number;
}

interface CouponConfig {
  couponCode: string;
  discountPercentage?: number;
  discountAmount?: number;
}

interface TimeWindow {
  startDate: number; // Unix timestamp
  endDate: number; // Unix timestamp
}

interface PackageGroup {
  groupName: string;
  packages: Package[];
  trialProducts: PreDefinedProduct[];
  mostPopular?: boolean;
}

interface Package {
  discountedProducts: PreDefinedProduct[];
  originPrice: number;
  discountedPrice: number;
}

interface PreDefinedProduct {
  productCode: string;
  quantity: number;
  originPrice?: number,
  discountedPrice?: number,
}

interface PromotionPriceProps {
  currentPrice?: number;
  discountPrice?: number;
  priceUnit?: string;
}

interface PromotionBannerProps {
  bannerStyle?: Record<string, string>;
  promotionPrefix: PromotionCampaignPrefix;
  hidePromotionPrice?: boolean;
}

enum PromotionCampaignPrefix {
  DEFAULT = 'DEFAULT',
  HOLIDAYPROMO = 'HOLIDAYPROMO',
  NEWYEAR2025 = 'NEWYEAR2025',
  BLACKFRIDAY = 'BLACKFRIDAY',
  NEWYEAR2026 = 'NEWYEAR2026',
}
